/**
 * Session, brand scoping and the route guards.
 *
 * The active brand lives on the server session and nowhere else. The client
 * never sends a brand id with a read, so a tampered client cannot look at
 * another brand. Switching brand is one POST followed by a full cache sweep,
 * because every number on screen belongs to the brand that was active when it
 * was fetched.
 *
 * Imported as `../lib/session`. The file is `.tsx` because it exports the two
 * route guards alongside the hooks.
 */
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { api, isApiError, onSessionRefreshed, onUnauthorized, refreshSession } from './api'
import { invalidateBrandScopedQueries, qk, queryClient } from './queryClient'
import type { LoginResult, Session, SessionBrand, TotpEnrollment, UserRole } from './types'

// ---------------------------------------------------------------------------
// Reading the session
// ---------------------------------------------------------------------------

export function useSessionQuery(): UseQueryResult<Session, unknown> {
  return useQuery({
    queryKey: qk.session(),
    queryFn: () => api.get<Session>('/session'),
    // The session is the one thing that must never be stale after a brand switch,
    // and it is cheap, so it refetches whenever a mounted consumer asks.
    staleTime: 15_000,
    retry: false,
    gcTime: Number.POSITIVE_INFINITY,
  })
}

export interface SessionState {
  session: Session | undefined
  user: Session['user'] | undefined
  brands: SessionBrand[]
  activeBrand: SessionBrand | undefined
  role: UserRole | undefined
  isAdmin: boolean
  /** May switch between the super admin view and the admin view. */
  isSuperAdmin: boolean
  /** Looking at every brand at once right now. False for everybody who is not a super admin. */
  superAdminView: boolean
  isLoading: boolean
  isSignedOut: boolean
  error: unknown
  refetch: () => void
}

/** Everything a page needs to know about who is signed in and what they are looking at. */
export function useSession(): SessionState {
  const query = useSessionQuery()
  const session = query.data
  const brands = useMemo(() => session?.brands ?? [], [session])
  const activeBrand = useMemo(
    () => brands.find((brand) => brand.id === session?.activeBrandId),
    [brands, session?.activeBrandId],
  )
  const isSignedOut = isApiError(query.error) && query.error.status === 401

  return {
    session,
    user: session?.user,
    brands,
    activeBrand,
    role: session?.role,
    isAdmin: session?.role === 'admin',
    isSuperAdmin: session?.user.superAdmin === true,
    superAdminView: session?.user.superAdminView === true,
    isLoading: query.isPending,
    isSignedOut,
    error: isSignedOut ? undefined : query.error,
    refetch: () => void query.refetch(),
  }
}

/** The brand every number on the current screen belongs to. */
export function useActiveBrand(): SessionBrand | undefined {
  return useSession().activeBrand
}

export function useIsAdmin(): boolean {
  return useSession().isAdmin
}

// ---------------------------------------------------------------------------
// Switching brand
// ---------------------------------------------------------------------------

/**
 * Switch the active brand.
 *
 * On success the new session is written straight into the cache and every
 * brand-scoped query is dropped and refetched. Inactive queries are removed
 * rather than invalidated, so returning to a page cannot flash the previous
 * brand's numbers for a frame.
 */
export function useBrandSwitch(): UseMutationResult<Session, unknown, string> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (brandId: string) => api.post<Session>('/session/brand', { brandId }),
    onSuccess: async (session) => {
      client.setQueryData(qk.session(), session)
      await invalidateBrandScopedQueries(client)
    },
  })
}

// ---------------------------------------------------------------------------
// The super admin's switch
// ---------------------------------------------------------------------------

/** The two views a super admin switches between. */
export type ConsoleView = 'super_admin' | 'admin'

/**
 * Switch between the super admin view (every brand at once) and the admin view.
 *
 * The answer is the session body, written straight into the cache, because
 * what changes is which screens exist rather than any brand's data: nothing
 * brand-scoped is refetched. The server keeps the choice on the person, so it
 * holds on every device they sign in on.
 */
export function useViewSwitch(): UseMutationResult<Session, unknown, ConsoleView> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (view: ConsoleView) => api.post<Session>('/session/view', { view }),
    onSuccess: (session) => {
      client.setQueryData(qk.session(), session)
    },
  })
}

// ---------------------------------------------------------------------------
// Signing in and out
// ---------------------------------------------------------------------------

export interface LoginInput {
  email: string
  password: string
}

export function useLogin(): UseMutationResult<LoginResult, unknown, LoginInput> {
  return useMutation({
    mutationFn: (input: LoginInput) => api.post<LoginResult>('/auth/login', input),
  })
}

export interface TotpInput {
  challengeId: string
  code: string
}

export function useVerifyTotp(): UseMutationResult<Session, unknown, TotpInput> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: TotpInput) => api.post<Session>('/auth/totp', input),
    onSuccess: (session) => {
      client.setQueryData(qk.session(), session)
    },
  })
}

export function useLogout(): UseMutationResult<void, unknown, void> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSettled: () => {
      client.clear()
    },
  })
}

// ---------------------------------------------------------------------------
// Forgetting a password
// ---------------------------------------------------------------------------

/**
 * Ask for a link to set a new password.
 *
 * The server answers the same for an address with an account and one without,
 * which is why this hook has no notion of "not found" and the screen must not
 * invent one. Success here means the request was accepted, not that an email
 * was sent, and the copy on the screen has to say the weaker thing.
 */
export function useForgotPassword(): UseMutationResult<ForgotResult, unknown, string> {
  return useMutation({
    mutationFn: (email: string) => api.post<ForgotResult>('/auth/forgot', { email: email.trim() }),
  })
}

export interface ForgotResult {
  ok: boolean
  message: string
}

export interface ResetSubject {
  email: string
  name: string
}

/**
 * Whose account a reset link belongs to.
 *
 * A query rather than a mutation, because the form asks it on load and the
 * answer decides what the screen renders. It reads without spending the link,
 * so a reload is free. `retry: false` because the only failure is a refusal and
 * retrying a refusal three times just makes the screen slow to say so.
 */
export function useResetSubject(token: string | null): UseQueryResult<ResetSubject, unknown> {
  return useQuery({
    queryKey: qk.resetSubject(token ?? ''),
    queryFn: () => api.post<ResetSubject>('/auth/reset/check', { token }),
    enabled: token !== null && token !== '',
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
  })
}

export interface ResetInput {
  token: string
  password: string
}

/**
 * Set the new password.
 *
 * Answers 204 and no session: a reset deliberately does not sign anybody in,
 * because an account with an authenticator still has to produce a code. The
 * caller sends the person to the sign-in form.
 */
export function useResetPassword(): UseMutationResult<void, unknown, ResetInput> {
  return useMutation({
    mutationFn: (input: ResetInput) => api.post<void>('/auth/reset', input),
  })
}

export function useTotpEnroll(): UseMutationResult<TotpEnrollment, unknown, void> {
  return useMutation({
    mutationFn: () => api.post<TotpEnrollment>('/auth/totp/enroll'),
  })
}

// ---------------------------------------------------------------------------
// Your own name and picture
// ---------------------------------------------------------------------------

export interface ProfileInput {
  name?: string
  tz?: string
  /** `null` takes the picture off. Leaving it out changes nothing. */
  avatarUrl?: string | null
  /** `null` goes back to translating nothing. Leaving it out changes nothing. */
  readingLanguage?: string | null
  /** Replaces the list. Leaving it out changes nothing. */
  schools?: string[]
}

/**
 * The session is refetched rather than patched from the response, because the
 * session body carries brands and knowledge state that this endpoint knows
 * nothing about, and hand-merging half of it is how a cache starts lying.
 */
export function useUpdateProfile(): UseMutationResult<unknown, unknown, ProfileInput> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: ProfileInput) => api.patch<unknown>('/me', input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: qk.session() })
    },
  })
}

/** Write a fresh session into the cache after signing in, so the shell renders at once. */
export function useAdoptSession(): (session: Session) => void {
  const client = useQueryClient()
  return useCallback(
    (session: Session) => {
      client.setQueryData(qk.session(), session)
    },
    [client],
  )
}

// ---------------------------------------------------------------------------
// Session expiry
// ---------------------------------------------------------------------------

/**
 * When any request comes back with "not signed in", drop the cached session so
 * the guard below redirects instead of leaving a dead page on screen.
 */
export function useSessionExpiryWatcher(): void {
  const client = useQueryClient()
  useEffect(
    () =>
      onUnauthorized(() => {
        client.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' })
        void client.invalidateQueries({ queryKey: qk.session() })
      }),
    [client],
  )
}

// ---------------------------------------------------------------------------
// Staying signed in (0228)
// ---------------------------------------------------------------------------

/** How long before the session's own end the app renews it. */
export const REFRESH_AHEAD_MS = 2 * 24 * 60 * 60 * 1000
/** How often an open tab looks at that, besides when it is shown again. */
export const REFRESH_CHECK_MS = 30 * 60 * 1000

/**
 * Whether the session is close enough to its end to renew now. A session that
 * names no end (an older server, a minted test session's own) plans nothing.
 */
export function shouldRefresh(expiresAt: string | null | undefined, now: number): boolean {
  if (typeof expiresAt !== 'string' || expiresAt === '') return false
  const end = Date.parse(expiresAt)
  if (Number.isNaN(end)) return false
  return end - now <= REFRESH_AHEAD_MS
}

/**
 * Keeps a signed-in person signed in (owner, 2026-11-03: "a refresh token
 * mechanism so new login token can be issued by the app itself on the
 * frontend"). The session lasts seven days. When fewer than two remain, the app
 * trades its refresh cookie for a new session, on load, when the tab is shown
 * again, and every half hour while it is open. A request that comes back signed
 * out renews it too (`lib/api.ts`), which covers a laptop that slept past the
 * end. The new session replaces the cached one.
 */
export function useSessionKeeper(expiresAt: string | null | undefined): void {
  const client = useQueryClient()
  useEffect(() => onSessionRefreshed((session) => client.setQueryData(qk.session(), session as Session)), [client])
  useEffect(() => {
    if (typeof expiresAt !== 'string' || expiresAt === '') return undefined
    const check = (): void => {
      if (shouldRefresh(expiresAt, Date.now())) void refreshSession()
    }
    check()
    const timer = window.setInterval(check, REFRESH_CHECK_MS)
    const onShown = (): void => {
      if (document.visibilityState === 'visible') check()
    }
    document.addEventListener('visibilitychange', onShown)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onShown)
    }
  }, [expiresAt])
}

/** Imperative sign-out used by error paths outside React. */
export function clearSessionCache(): void {
  queryClient.clear()
}

// ---------------------------------------------------------------------------
// Route guards
// ---------------------------------------------------------------------------

export interface GuardProps {
  children: ReactNode
  /** Rendered while the session is loading. The shell passes a full-page skeleton. */
  fallback?: ReactNode
  /** Rendered when the check fails for a reason other than being signed out. */
  denied?: ReactNode
}

/**
 * Anything behind this needs a signed-in session. A signed-out visitor is sent
 * to the sign-in screen with the page they wanted remembered, so they land back
 * where they were trying to go.
 */
export function RequireAuth({ children, fallback = null }: GuardProps): JSX.Element {
  const location = useLocation()
  const { isLoading, isSignedOut, session } = useSession()
  useSessionExpiryWatcher()
  useSessionKeeper(session?.sessionExpiresAt)

  if (isLoading) return <>{fallback}</>
  if (isSignedOut || !session) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  }
  return <>{children}</>
}

/** Admin-only surfaces. A member sees a plain explanation rather than a dead end. */
export function RequireAdmin({ children, fallback = null, denied }: GuardProps): JSX.Element {
  const { isLoading, isAdmin, session, isSignedOut } = useSession()
  const location = useLocation()

  if (isLoading) return <>{fallback}</>
  if (isSignedOut || !session) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  }
  if (!isAdmin) return <>{denied ?? <AdminOnlyNotice />}</>
  return <>{children}</>
}

function AdminOnlyNotice(): JSX.Element {
  return (
    <div className="mx-auto max-w-lg py-16 text-center">
      <h1 className="text-xl font-semibold text-ink">This page is for admins</h1>
      <p className="mt-2 text-sm text-ink-muted">
        Quota policies, account health and brand setup are limited to admin accounts because a change
        here affects everyone who sends. Ask an admin if you need something adjusted.
      </p>
    </div>
  )
}
